/* eslint-disable max-len */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Spinner from '../../../shared/components/Spinner/Spinner';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import decodeHtmlEntities from '../../../shared/utils/decodeHtmlEntities';
import getExerciseInfoCategory from '../../../shared/utils/getExerciseInfoCategory';
import {
  logReduxPresaveAddInfo, logReduxPresaveDescription, updateProgrammedCalendarItem, swapProgrammedItemOnChildCalendar,
  updateSavedWorkoutItem,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const HeaderRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 12px;
`;

const ExerciseName = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  color: #444444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  color: #77A7FF;
  white-space: nowrap;
  flex-shrink: 0;
`;

const FieldSection = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
`;

const SectionLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  color: #444444;
`;

const HelperText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  margin-top: 2px;
  color: #444444;
`;

const TextArea = styled('textarea')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  width: 100%;
  margin-top: 10px;
  border: 3px solid #E0E0E0;
  border-radius: 5px;
  padding: 10px;
  min-height: ${(props) => (props.minHeight ? props.minHeight : '120px')};
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  margin-top: 16px;
`;

const NewCalendarButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  background: #444444;
  color: white;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  user-select: none;
  border: 1px solid #444444;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const ErrorText = styled(Text)`
  color: #E53935;
  font-size: 12px;
  margin-top: 8px;
`;

const ManageCalendarsButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  color: #444444;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const RevertTextContainer = styled('div')`
display: flex;
width: 100%;
justify-content: flex-end;
gap: 4px;
align-items: center;
margin-top: 5px;
transition: all 250ms ease-in-out;
cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const SpinnerWrapper = styled('div')`
display: flex;
min-height: 229px;

`;

const AddDescriptionModal = ({
  closeModal,
}) => {
  const [additionalInfoText, setAdditionalInfoText] = useState('');
  const [descriptionText, setDescriptionText] = useState('');
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const preSaveDescription = useSelector((state) => state.calendar.data.preSaveDescription);
  const preSaveAdditionalInfo = useSelector((state) => state.calendar.data.preSaveAddInfo);
  const isCalendarItemUpdating = useSelector((state) => state.calendar.ui.isCalendarItemUpdating);
  const programType = useSelector((state) => state.calendar.data.programType);
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const nonDateBasedProgramItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);

  const category = getExerciseInfoCategory(modalData);
  const showAdditionalInfo = category === 'additionalInfo' || category === 'both';
  const showDescription = category === 'description' || category === 'both';

  // Description is required for note / warm_up / cooldown, which are exactly the description-only category.
  const requiresDescription = category === 'description';
  const isDescriptionEmpty = !descriptionText || descriptionText.trim() === '';
  const showRequiredError = requiresDescription && isDescriptionEmpty;

  useEffect(() => {
    const matchedDescription = preSaveDescription?.find((desc) => desc.id === modalData?.id);
    if (modalData?.preSave && matchedDescription) {
      setDescriptionText(decodeHtmlEntities(matchedDescription.content) || '');
    } else {
      setDescriptionText(decodeHtmlEntities(modalData?.description) || '');
    }

    const matchedInfo = preSaveAdditionalInfo?.find((info) => info.id === modalData?.id);
    if (modalData?.preSave && matchedInfo) {
      setAdditionalInfoText(decodeHtmlEntities(matchedInfo.content) || '');
    } else {
      setAdditionalInfoText(decodeHtmlEntities(modalData?.additionalInfo || modalData?.exercise?.additionalInfo) || '');
    }
  }, [modalData]);

  const dispatch = useDispatch();

  const formatDate = () => `${moment(modalData?.effectiveDate || selectedDate).format('YYYY-MM-DD')}`;
  const formatHeaderDate = () => `${moment(modalData?.effectiveDate || selectedDate).format('MMMM Do, YYYY')}`;

  const handleSave = () => {
    if (!modalData.preSave) {
      const nonSwappedItemFromParentCalendar = modalData.isInherited && !modalData.swap;
      const dateKey = formatDate();
      const candidateFreshItem = programType === 'free-form-program'
        ? nonDateBasedProgramItems?.[modalData.dayNumber]?.exercises?.[modalData.itemIndex]
        : calendarItems?.[dateKey]?.exercises?.[modalData.itemIndex];
      const freshItem = (candidateFreshItem && candidateFreshItem.assignedId === modalData.assignedId)
        ? candidateFreshItem
        : null;
      const sourceItem = freshItem ? { ...modalData, ...freshItem } : modalData;
      const updateItem = {
        ...sourceItem,
        assignedId: modalData.assignedId,
        calendarId: modalData.calendarId,
        date: dateKey,
        order: modalData.itemIndex,
        sets: sourceItem.sets,
        coachCompletion: sourceItem.coachCompletion,
        eachSide: sourceItem.eachSide,
        type: modalData.type,
        trackPeakPower: sourceItem.trackPeakPower,
        restTime: sourceItem.restTime ? sourceItem.restTime : null,
      };

      if (programType === 'free-form-program') {
        updateItem.dayNumber = modalData.dayNumber;
      }
      if (modalData.type === 'tag') {
        updateItem.tagId = modalData.tag.id;
      } else {
        updateItem.exerciseId = modalData.exercise.id;
      }

      // Persist whichever field(s) this item type exposes; preserve any hidden field from the source item.
      if (showAdditionalInfo) {
        if (additionalInfoText) {
          updateItem.additionalInfo = additionalInfoText;
        } else {
          delete updateItem.additionalInfo;
        }
      } else if (sourceItem.additionalInfo) {
        updateItem.additionalInfo = sourceItem.additionalInfo;
      }

      if (showDescription) {
        updateItem.description = descriptionText || '';
      } else if (sourceItem.description) {
        updateItem.description = sourceItem.description;
      } else if (sourceItem.description === '') {
        delete updateItem.description;
      }

      if (sourceItem.pacing) {
        updateItem.pacing = sourceItem.pacing;
      } else if (sourceItem.pacing === null) {
        delete updateItem.pacing;
      }
      if (sourceItem.trackRepCount) {
        updateItem.trackRepCount = sourceItem.trackRepCount;
      }
      if (sourceItem.trackVolumeLoad) {
        updateItem.trackVolumeLoad = sourceItem.trackVolumeLoad;
      }
      if (sourceItem.repsOnly) {
        updateItem.repsOnly = sourceItem.repsOnly;
      }
      if (sourceItem.repType) {
        updateItem.repType = sourceItem.repType;
      }
      if (sourceItem.forceMaxUpdate) {
        updateItem.forceMaxUpdate = sourceItem.forceMaxUpdate;
      }
      if (sourceItem.maxTrackingDisabled) {
        updateItem.maxTrackingDisabled = sourceItem.maxTrackingDisabled;
      }
      if (sourceItem.tempo) {
        updateItem.tempo = sourceItem.tempo;
      }
      if (sourceItem.trackBarSpeed) {
        updateItem.trackBarSpeed = sourceItem.trackBarSpeed;
      }
      if (sourceItem.repIntensityUnit) {
        updateItem.repIntensityUnit = sourceItem.repIntensityUnit;
      }
      if (modalData.type === 'cardio' || modalData.type === 'lift' || modalData.type === 'tag') {
        if (sourceItem.completionOnly) {
          updateItem.completionOnly = sourceItem.completionOnly;
        }
      }
      if (programType === 'free-form-program') {
        const reorder = false;
        dispatch(updateSavedWorkoutItem(currentUser.accountCode, updateItem, modalData.itemIndex, modalData.dayNumber, reorder, {
          onSuccess: () => {
            closeModal();
          },
          onError: () => {},
        }));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          const reorder = false;
          dispatch(swapProgrammedItemOnChildCalendar(currentUser.accountCode, updateItem, modalData.itemIndex, formatDate(), reorder, {
            onSuccess: () => {
              closeModal();
            },
            onError: () => {},
          }));
        } else {
          const reorder = false;
          dispatch(updateProgrammedCalendarItem(currentUser.accountCode, updateItem, modalData.itemIndex, formatDate(), reorder, {
            onSuccess: () => {
              closeModal();
            },
            onError: () => {},
          }));
        }
      }
    } else {
      if (showAdditionalInfo) {
        dispatch(logReduxPresaveAddInfo(
          modalData.id,
          additionalInfoText,
        ));
      }
      if (showDescription) {
        dispatch(logReduxPresaveDescription(
          modalData.id,
          descriptionText,
        ));
      }
      closeModal();
    }
  };

  const handleRevertToOriginalDescription = () => {
    setDescriptionText(decodeHtmlEntities(modalData?.exercise?.description));
  };

  return (
    <ModalContainer>
      <HeaderRow>
        <ExerciseName title={modalData?.title}>{modalData?.title}</ExerciseName>
        {programType !== 'free-form-program' && <DateText>{formatHeaderDate()}</DateText>}
      </HeaderRow>
      {isCalendarItemUpdating ? (<SpinnerWrapper><Spinner /></SpinnerWrapper>) : (
        <>
          {showAdditionalInfo && (
            <FieldSection>
              <SectionLabel>Additional Info</SectionLabel>
              <HelperText>This will appear for only this assignment (Limit 255 Characters)</HelperText>
              <TextArea
                value={additionalInfoText}
                minHeight='70px'
                onChange={(e) => {
                  // Additional info has a 255 character limit
                  if (e.target.value.length <= 255) {
                    setAdditionalInfoText(e.target.value);
                  }
                }}
                autoFocus
                maxLength={255}
              />
            </FieldSection>
          )}
          {showDescription && (
            <FieldSection>
              <SectionLabel>Description</SectionLabel>
              <HelperText>This comes from your database, editing will only apply to this instance</HelperText>
              <TextArea
                value={descriptionText}
                minHeight='150px'
                onChange={(e) => setDescriptionText(e.target.value)}
                autoFocus={!showAdditionalInfo}
              />
              {modalData?.exercise?.description && descriptionText !== decodeHtmlEntities(modalData?.exercise?.description) && (
                <RevertTextContainer onClick={() => handleRevertToOriginalDescription()}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={12}
                    icon='restart'
                    color='#77A7FF'
                  />
                  <Text fontSize='16px' color='#77A7FF'>Revert to original</Text>
                </RevertTextContainer>
              )}
              {showRequiredError && (
                <ErrorText>Description cannot be empty.</ErrorText>
              )}
            </FieldSection>
          )}
        </>
      )}
      <ButtonsContainer>
        <NewCalendarButton
          disabled={showRequiredError}
          onClick={() => {
            if (!isCalendarItemUpdating && !showRequiredError) {
              handleSave();
            }
          }}
        >
          SAVE
        </NewCalendarButton>
        <ManageCalendarsButton onClick={() => (!isCalendarItemUpdating ? closeModal() : null)}>
          CANCEL
        </ManageCalendarsButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

AddDescriptionModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default AddDescriptionModal;
